import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../hooks/useAuth'
import { API_BASE_URL } from '../utils/api'
import { DollarIcon } from '../components/Icons'

interface Invoice {
  id: string
  date: string
  amount: number
  status: string
  pdf_url?: string
}

interface Membership {
  tier_id: number | null
  stripe_customer_id: string | null
  stripe_subscription_id: string | null
  subscription_status: string | null
  stripe_subscription_status: string | null
  subscription_current_period_end: string | null
  subscription_canceled_at: string | null
  subscription_override_until: string | null
  subscription_override_reason: string | null
  subscription_discount_percent: number | null
  has_active_override: boolean
  is_trial_active: boolean
  trial_days_remaining: number
  trial_ends_at: string | null
  current_tier: {
    id: number
    name: string
    price: number
  } | null
}

const STATUS_STYLES: Record<string, string> = {
  active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  trialing: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  override: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  past_due: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  canceled: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  inactive: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
}

const BrokerMembershipSettings: React.FC = () => {
  const { t, i18n } = useTranslation()
  const { isBroker } = useAuth()
  const [membership, setMembership] = useState<Membership | null>(null)
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [loading, setLoading] = useState(true)
  const [portalLoading, setPortalLoading] = useState(false)
  const [syncLoading, setSyncLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [portalError, setPortalError] = useState<string | null>(null)

  const authHeader = () => ({
    Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
  })

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const [membershipRes, invoicesRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/membership/manage/current`, { headers: authHeader() }),
        fetch(`${API_BASE_URL}/api/membership/manage/invoices`, { headers: authHeader() }),
      ])

      if (membershipRes.ok) {
        const data = await membershipRes.json()
        setMembership(data.membership)
      }

      if (invoicesRes.ok) {
        const data = await invoicesRes.json()
        setInvoices(data.invoices || [])
      }
    } catch {
      setError(t('membership.page.loadError', 'Failed to load membership information.'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (isBroker) fetchData()
  }, [isBroker, fetchData])

  const handleManageBilling = async () => {
    setPortalLoading(true)
    setPortalError(null)
    try {
      const res = await fetch(`${API_BASE_URL}/api/membership/manage/billing-portal`, {
        headers: authHeader(),
      })
      const data = await res.json()
      if (res.ok && data.portal_url) {
        window.open(data.portal_url, '_blank')
      } else {
        setPortalError(data.message || t('membership.page.portalError', 'Could not open billing portal. Please contact support.'))
      }
    } catch {
      setPortalError(t('membership.page.portalRetry', 'Could not open billing portal. Please try again.'))
    } finally {
      setPortalLoading(false)
    }
  }

  const handleSync = async () => {
    setSyncLoading(true)
    try {
      const res = await fetch(`${API_BASE_URL}/api/membership/manage/sync-from-stripe`, {
        method: 'POST',
        headers: authHeader(),
      })
      if (res.ok) {
        await fetchData()
      }
    } finally {
      setSyncLoading(false)
    }
  }

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)

  const formatDate = (iso: string) => {
    const d = new Date(iso)
    if (d.getFullYear() >= 2099) return t('membership.page.indefinite', 'Indefinite')
    return d.toLocaleDateString(i18n.language === 'fr' ? 'fr-CA' : 'en-CA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const statusLabel = () => {
    if (!membership) return t('membership.page.statusInactive', 'Inactive')
    if (membership.has_active_override) return t('membership.page.statusOverride', 'Access granted')
    if (membership.is_trial_active)
      return t('membership.page.statusTrialing', 'Trial — {{days}} days left', { days: membership.trial_days_remaining })
    if (membership.subscription_canceled_at && membership.stripe_subscription_status !== 'canceled')
      return t('membership.page.statusCanceling', 'Cancels at period end')
    return membership.stripe_subscription_status || t('membership.page.statusInactive', 'Inactive')
  }

  const statusBadgeClass = () => {
    if (membership?.has_active_override) return STATUS_STYLES.override
    const key = membership?.is_trial_active
      ? 'trialing'
      : (membership?.stripe_subscription_status ?? 'inactive')
    return STATUS_STYLES[key] ?? STATUS_STYLES.inactive
  }

  const renewalInfo = () => {
    if (!membership) return null
    if (membership.has_active_override && membership.subscription_override_until) {
      return { label: t('membership.page.accessUntil', 'Access until'), date: membership.subscription_override_until }
    }
    if (membership.is_trial_active && membership.trial_ends_at) {
      return { label: t('membership.page.trialEnds', 'Trial ends'), date: membership.trial_ends_at }
    }
    if (membership.subscription_canceled_at && membership.subscription_current_period_end) {
      return { label: t('membership.page.accessUntil', 'Access until'), date: membership.subscription_current_period_end }
    }
    if (membership.subscription_current_period_end && membership.stripe_subscription_status === 'active') {
      return { label: t('membership.page.nextRenewal', 'Next renewal'), date: membership.subscription_current_period_end }
    }
    return null
  }

  if (!isBroker) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {t('membership.page.accessDenied', 'Access Denied')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t('membership.page.brokerOnly', 'Membership settings are only available for broker accounts.')}
        </p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 dark:border-white" />
      </div>
    )
  }

  const renewal = renewalInfo()

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">

      {/* Header */}
      <div className="flex items-center space-x-3">
        <div className="w-12 h-12 bg-gradient-to-r from-gray-800 to-gray-900 rounded-xl flex items-center justify-center">
          <DollarIcon className="h-7 w-7 text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {t('membership.title', 'Membership')}
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-0.5 text-sm">
            {t('membership.page.subtitle', 'Manage your subscription via Stripe')}
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-red-800 dark:text-red-300 text-sm">{error}</p>
        </div>
      )}

      {/* Status Card */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-5">
          {t('membership.page.currentPlan', 'Current Plan')}
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {/* Plan name */}
          <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
              {t('membership.page.plan', 'Plan')}
            </p>
            <p className="text-base font-semibold text-gray-900 dark:text-white">
              {membership?.current_tier?.name || t('membership.page.noActivePlan', 'No active plan')}
            </p>
            {membership?.current_tier?.price ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {formatCurrency(membership.current_tier.price)}{t('membership.page.perMonth', '/mo')}
              </p>
            ) : null}
          </div>

          {/* Status */}
          <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
              {t('membership.page.status', 'Status')}
            </p>
            <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${statusBadgeClass()}`}>
              {statusLabel()}
            </span>
          </div>

          {/* Renewal / Trial end */}
          <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            {renewal ? (
              <>
                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                  {renewal.label}
                </p>
                <p className="text-base font-semibold text-gray-900 dark:text-white">
                  {formatDate(renewal.date)}
                </p>
              </>
            ) : (
              <>
                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                  {t('membership.page.billing', 'Billing')}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">—</p>
              </>
            )}
          </div>
        </div>

        {/* Admin override notice */}
        {membership?.has_active_override && (
          <div className="mt-4 flex items-start gap-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg px-4 py-3">
            <svg className="w-5 h-5 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
            <div className="text-sm">
              <p className="font-semibold text-purple-800 dark:text-purple-300">
                {t('membership.page.overrideTitle', 'Access granted by administrator')}
              </p>
              {membership.subscription_override_reason && (
                <p className="text-purple-700 dark:text-purple-400 mt-0.5">{membership.subscription_override_reason}</p>
              )}
              {membership.subscription_override_until && (
                <p className="text-purple-600 dark:text-purple-500 mt-0.5 text-xs">
                  {t('membership.page.overrideExpires', 'Expires {{date}}', { date: formatDate(membership.subscription_override_until) })}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Manage Billing Button */}
        <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3">
          <button
            onClick={handleManageBilling}
            disabled={portalLoading}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gray-900 hover:bg-gray-800 disabled:bg-gray-400 dark:disabled:bg-gray-600 text-white font-semibold rounded-lg transition-colors text-sm"
          >
            {portalLoading ? (
              <>
                <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                {t('membership.page.opening', 'Opening…')}
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                {t('membership.page.manageBilling', 'Manage Billing')}
              </>
            )}
          </button>

          <button
            onClick={handleSync}
            disabled={syncLoading}
            className="inline-flex items-center gap-2 px-4 py-3 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            {syncLoading ? (
              <span className="inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
            ) : (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            )}
            {t('membership.page.refreshStatus', 'Refresh status')}
          </button>
        </div>

        {portalError && (
          <p className="mt-3 text-sm text-red-600 dark:text-red-400">{portalError}</p>
        )}

        <p className="mt-4 text-xs text-gray-400 dark:text-gray-500">
          {t('membership.page.stripeNote', 'Subscription management, plan changes, payment methods, and invoices are handled securely through Stripe.')}
        </p>
      </div>

      {/* Recent Invoices */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-5">
          {t('membership.page.recentInvoices', 'Recent Invoices')}
        </h2>

        {invoices.length === 0 ? (
          <div className="text-center py-10 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <svg className="mx-auto h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{t('membership.page.noInvoices', 'No invoices yet')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead>
                <tr>
                  {[t('membership.page.date', 'Date'), t('membership.page.amount', 'Amount'), t('membership.page.status', 'Status'), ''].map(h => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {invoices.map(invoice => (
                  <tr key={invoice.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                    <td className="px-4 py-3 text-sm text-gray-900 dark:text-white whitespace-nowrap">
                      {formatDate(invoice.date)}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white whitespace-nowrap">
                      {formatCurrency(invoice.amount)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${
                        invoice.status === 'paid'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                          : invoice.status === 'open'
                          ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                          : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                      }`}>
                        {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {invoice.pdf_url ? (
                        <a
                          href={invoice.pdf_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-gray-900 dark:text-gray-300 hover:underline"
                        >
                          {t('membership.page.downloadPdf', 'Download PDF')}
                        </a>
                      ) : (
                        <span className="text-sm text-gray-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

    </div>
  )
}

export default BrokerMembershipSettings
